// src/validation/secureSignalsHealth.js
// Secure Signals (a3p) health rollup.
//
// Reconciles what Publisher Settings DECLARES (which secure-signal adapters the
// app is integrated with — e.g. UID2, LiveRamp) against what is actually
// OBSERVED reaching Google Ad Manager in the decoded `a3p` param on each GAM
// /gampad/ads request. The publisher settings card only proves intent; this
// proves delivery, per request, as a coverage rate.
//
// Mirrors the shape of computeBidHealthByAppPlatform / reconstructOmidSessions
// so it can render as a peer "health rollup" panel in the lifecycle view.

const isGamRow = (r) => r?.requestType === "google" || r?.type === "google";

const getSecureSignals = (r) =>
    r?.validData?.secureSignals || r?.validData?.expanded?.secureSignals || null;

// Short, human label for a GAM request used in drill-in tooltips.
function reqLabel(r) {
    try {
        const u = new URL(r.url);
        const iu = u.searchParams.get("iu");
        if (iu) return iu;
        const corr = u.searchParams.get("correlator");
        if (corr) return `correlator ${corr}`;
        return u.pathname;
    } catch {
        return r?._k != null ? `req ${r._k}` : "GAM request";
    }
}

/**
 * @param {Array}  rows             all captured rows (GAM requests are filtered out internally)
 * @param {string[]} declaredAdapters trackable secure-signal adapter names from Publisher Settings
 *                                   (already filtered to the ones that travel via a3p, e.g. UID2 / LiveRamp)
 * @returns {null | {
 *   adapters: Array<{ name, reaching, total, pct, severity, missing: Array<{key, iu}> }>,
 *   total: number, gamTotal: number, declared: string[], overall: 'pass'|'warn'|'fail'
 * }}
 */
export function buildSecureSignalsHealth(rows = [], declaredAdapters = []) {
    const declared = (declaredAdapters || []).filter(Boolean);
    if (declared.length === 0) return null; // nothing declared → nothing to verify

    const gamRows = (rows || []).filter(isGamRow);
    // Only requests where secure-signal decoding was attempted count toward coverage.
    const considered = gamRows.filter((r) => getSecureSignals(r));
    const total = considered.length;

    if (total === 0) {
        // Declared adapters exist but no GAM request has been validated yet.
        return { adapters: [], total: 0, gamTotal: gamRows.length, declared, overall: "pass" };
    }

    const adapters = declared.map((name) => {
        let reaching = 0;
        const missing = [];
        considered.forEach((r) => {
            const ss = getSecureSignals(r);
            const ok = !!(ss && ss.present && Array.isArray(ss.detected) && ss.detected.includes(name));
            if (ok) reaching++;
            else missing.push({ key: r._k ?? r.id ?? null, iu: reqLabel(r) });
        });
        const pct = total ? Math.round((reaching / total) * 100) : 0;
        const severity = reaching === 0 ? "fail" : reaching < total ? "warn" : "pass";
        return { name, reaching, total, pct, severity, missing };
    });

    const overall = adapters.some((a) => a.severity === "fail")
        ? "fail"
        : adapters.some((a) => a.severity === "warn")
            ? "warn"
            : "pass";

    return { adapters, total, gamTotal: gamRows.length, declared, overall };
}

export default buildSecureSignalsHealth;
